namespace Solovis.Reports.Util {

    export interface SubReportCrawlContext {
        depth: number;
        labelPath: string[];
    }


    /**
     * SubReportCrawler will start at the root report and call a callback once the report is considered done.
     * The callback is responsible for asking SubReportCrawler to process the next report. If there is a tabbed
     * sub-report list, then the crawler will iterate through the tabs and wait for each report to indicate that it
     * is done.
     */
    export class SubReportCrawler {

        private _rootReportControl: Solovis.Reports.ReportControl;
        private _reportControlCallback: (reportControl: Solovis.Reports.ReportControl, crawlContext: SubReportCrawlContext) => any;        
        private _allDoneCallback: () => any;

        constructor(options) {
            this._rootReportControl = options.reportControl;
            this._reportControlCallback = options.reportControlCallback;
            this._allDoneCallback = options.allDoneCallback;
        }

        public async start() {
            let crawlContext: SubReportCrawlContext = {
                depth: 0,
                labelPath: []
            };

            await this._processReportControl(this._rootReportControl, crawlContext);            

            if (this._allDoneCallback) {
                this._allDoneCallback();
            }
        }

        private async _processReportControl(reportControl: Solovis.Reports.ReportControl, crawlContext: SubReportCrawlContext) {
            await this._waitForReportControl(reportControl);
            await this._invokeCallbackForReportControl(reportControl, crawlContext);
            await this._crawlReportChildItems(reportControl, crawlContext);
        }


        private _invokeCallbackForReportControl(reportControl: Solovis.Reports.ReportControl, crawlContext: SubReportCrawlContext): Promise<any> {
            let self = this;

            let promise = new Promise(function (resolve, reject) {

                if (self._reportControlCallback) {
                    let callbackResponse = self._reportControlCallback(reportControl, crawlContext);

                    if (callbackResponse && callbackResponse.then) {
                        callbackResponse
                            .then(() => resolve())
                            .catch((error) => {
                                console.log("SubReportCrawler: callback failed");
                                console.log(error);

                                // We allow crawling of other tables to continue on in this situation.
                                resolve();
                            });
                    }
                    else {
                        resolve();
                    }
                }
                else {
                    resolve();
                }
            });

            return promise;
        }


        private _createNewSubCrawlContext(crawlContext: SubReportCrawlContext, newLabel: string) {
            let newCrawlContext: SubReportCrawlContext = {
                depth: crawlContext.depth + 1,
                labelPath: newLabel ? crawlContext.labelPath.concat(newLabel) : crawlContext.labelPath
            };

            return newCrawlContext;
        }


        private async _crawlReportChildItems(reportControl: Solovis.Reports.ReportControl, crawlContext: SubReportCrawlContext) {
            let elementNames = reportControl.getElementNameList();
            
            for (let elementName of elementNames) {
                let partControl = reportControl.getReportPartControl(elementName);
                if (partControl instanceof Solovis.Reports.Parts.SubReportControl) {
                    let subReportControl = partControl as Solovis.Reports.Parts.SubReportControl;

                    await this._processReportControl(subReportControl.subReportControl, this._createNewSubCrawlContext(crawlContext, null));
                }
                else if (partControl instanceof Solovis.Reports.Parts.TabListSubReportControl) {                    
                    let tabListSubReportControl = partControl as Solovis.Reports.Parts.TabListSubReportControl;

                    let tabCount = tabListSubReportControl.getTabCount();
                    
                    for (let i = 0; i < tabCount; i++) {
                        tabListSubReportControl.activateTabByIndex(i);

                        await Solovis.Async.delay(100);

                        let tabLabel = tabListSubReportControl.tryGetTabLabelByIndex(i);

                        let tabSubReportControl = tabListSubReportControl.tryGetTabSubReportControlByIndex(i);
                        if (tabSubReportControl) {
                            await this._processReportControl(tabSubReportControl, this._createNewSubCrawlContext(crawlContext, tabLabel));
                        }
                    }
                }
            }
        }


        private _waitForReportControl(reportControl: Solovis.Reports.ReportControl): Promise<any> {
            // Hardcoded to 10 minute timeout for now.
            let maxWaitTimeInMs = 1000 * 60 * 10;

            let remainingTimeInMs = maxWaitTimeInMs;

            let promise = new Promise(function (resolve, reject) {
                let intervalMs = 500;

                let intervalHandle = setInterval(
                    function () {
                        if (!reportControl.isRunning()) {
                            clearInterval(intervalHandle);
                            resolve(true);
                        }
                        else {
                            remainingTimeInMs -= intervalMs;

                            if (remainingTimeInMs < 0) {
                                reject("Timeout");
                            }
                        }

                    },
                    intervalMs);
            });

            return promise;
        }
    }


}